import { useState, useEffect, useRef } from "react";
import { Search, Plus, X, CalendarDays, Trash } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate, dateIST } from "@emr/shared/india";
import { FORMULATIONS, isValidFrequency, normalizeFrequency, type TaperStep } from "@emr/shared";
import { useDrugs } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { DischargeFrequencyInput } from "./DischargeFrequencyInput";
import { useDropdownNav } from "@/hooks/useDropdownNav";
import type { DischargeMedicineRow } from "@/lib/queries";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  medicine: DischargeMedicineRow | null;
  onSave: (m: Partial<DischargeMedicineRow>) => void;
  onDelete: () => void;
}

// ----------------------------------------------------------------------------
// PRIVATE CLONE for the discharge summaries feature (house convention: the
// discharge module owns its components so it can be fine-tuned without
// touching the EMR visit feature). Cloned from features/emr/
// EditMedicineDialog.tsx 2026-10-16 — retyped for DischargeMedicineRow
// (dischargeSummaryId instead of prescriptionId). Editing this file must
// never require touching the EMR original, and vice versa.
// ----------------------------------------------------------------------------

// ---- Draft state (mirrors the EMR dialog's DraftMedicine / DraftTaperRow) ----
interface DraftTaperRow {
  key: string;
  fromDate: string; // YYYY-MM-DD
  frequency: string;
  days: string;
  instructions: string;
  name: string;
  strength: string;
  composition: string | null;
  formulation: DischargeMedicineRow["formulation"] | null;
  drugId: string | null;
}

interface DraftMedicine {
  drugId: string | null;
  name: string;
  composition: string | null;
  formulation: DischargeMedicineRow["formulation"];
  strength: string;
  frequency: string;
  taper: DraftTaperRow[] | null;
  days: string;
  instructions: string;
  perStrength: boolean;
}

// Same grid templates as the EMR dialog so the boxes line up exactly.
const TAPER_GRID =
  "grid grid-cols-[140px_140px_64px_120px_minmax(120px,1fr)_32px_32px] items-center gap-2";
const TAPER_GRID_DRUG =
  "grid grid-cols-[180px_120px_120px_56px_110px_minmax(110px,1fr)_32px_32px] items-center gap-2";

function daysFromDuration(duration: string | null | undefined): string {
  if (!duration) return "";
  const m = duration.match(/^(\d+)/);
  return m ? m[1] : "";
}

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

function stepToDate(fromDate: string, days: string): string | null {
  const n = parseInt(days, 10);
  if (!fromDate || !n || n < 1) return null;
  return addDays(fromDate, n - 1);
}

/**
 * Re-chain the protocol rows AFTER index `fromIdx`: each following row's
 * From date becomes the previous row's To date + 1 day (its To date is
 * derived from From + days - 1 at render, so both ends recompute). Rows
 * before `fromIdx` are untouched. A following row whose predecessor has no
 * computable To date (blank from-date or days mid-edit) keeps its current
 * From date until the predecessor is complete again.
 */
function rechainTaperRows(rows: DraftTaperRow[], fromIdx: number): DraftTaperRow[] {
  const next = [...rows];
  for (let i = fromIdx + 1; i < next.length; i++) {
    const prevTo = stepToDate(next[i - 1].fromDate, next[i - 1].days);
    if (prevTo) next[i] = { ...next[i], fromDate: addDays(prevTo, 1) };
  }
  return next;
}

/**
 * Per-row drug search box for the taper grid (per-strength mode only).
 * Identical behaviour to the EMR dialog's — kept private to this clone.
 */
function TaperRowDrugSearch({
  rowKey,
  value,
  onPick,
}: {
  rowKey: string;
  value: string;
  onPick: (d: { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }) => void;
}) {
  const [q, setQ] = useState(value);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { setQ(value); }, [value, rowKey]);
  // Debounced (250ms): one /drugs?q= per typing pause, not per keystroke.
  const debouncedQ = useDebouncedValue(q, 250);
  const { data: drugsData, isLoading } = useDrugs(debouncedQ.length >= 3 ? debouncedQ : "");
  const drugs = (drugsData && "rows" in drugsData ? drugsData.rows : Array.isArray(drugsData) ? drugsData : []) as { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }[];

  const taperResults = drugs.slice(0, 10);
  const taperListCount = taperResults.length + 1;

  const { highlight: taperHighlight, highlightRef: taperHighlightRef, onKeyDown: onTaperSearchKeyDown } = useDropdownNav({
    count: open && q.length >= 3 && !isLoading ? taperListCount : 0,
    onSelect: (i) => {
      if (i < taperResults.length) onPick(taperResults[i]);
      else onPick({ id: "", name: q, composition: null, formulation: null, strength: "" });
      setOpen(false);
    },
    inputRef: ref,
    onEscape: () => setOpen(false),
  });

  return (
    <div className="relative">
      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
      <Input
        ref={ref}
        className="h-8 pl-7 text-xs"
        value={q}
        onChange={(e) => {
          const v = e.target.value;
          setQ(v);
          onPick({ id: "", name: v, composition: null, formulation: null, strength: "" });
          setOpen(v.length >= 3);
        }}
        onFocus={() => q.length >= 3 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onTaperSearchKeyDown}
        placeholder="Search drug / strength"
      />
      {open && q.length >= 3 && (
        <div className="visible-scrollbar absolute z-50 mt-1 max-h-52 w-full overscroll-contain overflow-y-auto rounded-md border border-cream-200 bg-cream-100 shadow-lg dark:border-dark-700 dark:bg-dark-800">
          {isLoading ? (
            <div className="p-2"><Skeleton className="h-5" /></div>
          ) : drugs.length === 0 ? (
            <button
              type="button"
              ref={taperHighlight === 0 ? taperHighlightRef : undefined}
              className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-xs hover:bg-cream-200 dark:hover:bg-dark-700 ${
                taperHighlight === 0 ? "bg-cream-200 dark:bg-dark-700" : ""
              }`}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); }}
            >
              <Search size={12} className="text-muted" /> No match — keep "{q}"
            </button>
          ) : (
            taperResults.map((d, idx) => (
              <button
                key={d.id}
                type="button"
                ref={idx === taperHighlight ? taperHighlightRef : undefined}
                className={`flex w-full flex-col px-3 py-1.5 text-left text-xs hover:bg-cream-200 dark:hover:bg-dark-700 ${
                  idx === taperHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                }`}
                onMouseDown={(e) => { e.preventDefault(); onPick(d); setOpen(false); }}
              >
                <span className="font-medium text-ink dark:text-cream-50">{d.name}</span>
                <span className="text-[10px] text-muted">
                  {[d.composition, d.formulation, d.strength].filter(Boolean).join(" · ")}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Convert an existing DischargeMedicineRow (with an optional taper) into a
 * DraftMedicine so the edit dialog opens with the same shape the add flow
 * uses. Taper steps (stored as TaperStep with `days: number`) are converted
 * back to the draft's string `days` so the grid inputs can edit them.
 */
function medicineToDraft(m: DischargeMedicineRow): DraftMedicine {
  const taper: DraftTaperRow[] | null = m.taper
    ? m.taper.map((s) => ({
        key: crypto.randomUUID(),
        fromDate: s.fromDate,
        frequency: s.frequency,
        days: String(s.days),
        instructions: s.instructions ?? "",
        name: s.name ?? "",
        strength: s.strength ?? "",
        composition: s.composition ?? null,
        formulation: (s.formulation as DischargeMedicineRow["formulation"]) ?? null,
        drugId: s.drugId ?? null,
      }))
    : null;
  return {
    drugId: m.drugId,
    name: m.name,
    composition: m.composition,
    formulation: (m.formulation ?? "Tablet") as DischargeMedicineRow["formulation"],
    strength: m.strength ?? "",
    frequency: m.frequency ?? "",
    taper,
    days: daysFromDuration(m.duration),
    instructions: m.instructions ?? "",
    // If the medicine has a taper with per-step drug overrides, start in
    // per-strength mode so the row-level drug boxes show.
    perStrength: taper ? taper.some((r) => r.drugId || r.name) : false,
  };
}

export function DischargeEditMedicineDialog({ open, onOpenChange, medicine, onSave, onDelete }: Props) {
  // `draft` is the editable form state (same shape as the EMR edit dialog).
  // An existing taper protocol loads straight into the editable grid (same
  // format as entry) — mirrors the EMR dialog rework 2026-10-30.
  const [draft, setDraft] = useState<DraftMedicine | null>(null);
  // Drug-name search (top of the dialog).
  const [nameQuery, setNameQuery] = useState("");
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  const nameSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && medicine) {
      setDraft(medicineToDraft(medicine));
      setNameQuery(medicine.name);
      setShowNameDropdown(false);
    }
  }, [open, medicine]);

  const set = <K extends keyof DraftMedicine>(k: K, v: DraftMedicine[K]) =>
    setDraft((prev) => (prev ? { ...prev, [k]: v } : prev));

  // ---- Drug-name search (live /drugs?q=…) ----
  // Debounced (250ms): one request per typing pause, not per keystroke.
  const debouncedNameQuery = useDebouncedValue(nameQuery, 250);
  const { data: drugsData, isLoading: drugsLoading } = useDrugs(debouncedNameQuery.length >= 3 ? debouncedNameQuery : "");
  const drugs = (drugsData && "rows" in drugsData ? drugsData.rows : Array.isArray(drugsData) ? drugsData : []) as { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }[];

  // Dropdown items: up to 10 drug matches + 1 "Keep as entered" entry.
  const editDrugResults = drugs.slice(0, 10);
  const editDrugListCount = editDrugResults.length + 1;

  const selectDrug = (d: { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }) => {
    setDraft((prev) => prev ? {
      ...prev,
      drugId: d.id,
      name: d.name,
      composition: d.composition,
      formulation: (d.formulation as DischargeMedicineRow["formulation"]) ?? prev.formulation ?? "Tablet",
      strength: d.strength ?? prev.strength,
    } : prev);
    setNameQuery(d.name);
    setShowNameDropdown(false);
  };

  const { highlight: editDrugHighlight, highlightRef: editDrugHighlightRef, onKeyDown: onEditDrugKeyDown } = useDropdownNav({
    count: showNameDropdown && nameQuery.length >= 3 && !drugsLoading ? editDrugListCount : 0,
    onSelect: (i) => {
      if (i < editDrugResults.length) selectDrug(editDrugResults[i]);
      else { setShowNameDropdown(false); }
    },
    inputRef: nameSearchRef,
    onEscape: () => setShowNameDropdown(false),
  });

  // ---- Taper grid helpers (same logic as the EMR dialog) ----
  const newTaperRow = (fromDate: string, inheritDrug: DraftMedicine, perStrength: boolean): DraftTaperRow => ({
    key: crypto.randomUUID(),
    fromDate,
    frequency: "",
    days: "",
    instructions: "",
    name: perStrength ? inheritDrug.name : "",
    strength: perStrength ? inheritDrug.strength : "",
    composition: perStrength ? inheritDrug.composition : null,
    formulation: perStrength ? inheritDrug.formulation : null,
    drugId: perStrength ? inheritDrug.drugId : null,
  });

  const enableTaper = () => {
    if (!draft) return;
    // An existing taper is loaded into the draft; enabling when one exists
    // must not wipe it (the checkbox is checked, not re-seeded).
    if (draft.taper) return;
    set("taper", [newTaperRow(dateIST(), draft, draft.perStrength)]);
  };
  const disableTaper = () => set("taper", null);

  const togglePerStrength = (checked: boolean) => {
    if (!draft) return;
    if (!checked) {
      setDraft({
        ...draft,
        perStrength: false,
        taper: (draft.taper ?? []).map((r) => ({ ...r, name: "", strength: "", composition: null, formulation: null, drugId: null })),
      });
    } else {
      setDraft({
        ...draft,
        perStrength: true,
        taper: (draft.taper ?? []).map((r) => ({ ...r, name: r.name || draft.name, strength: r.strength || draft.strength, composition: r.composition ?? draft.composition, formulation: r.formulation ?? draft.formulation, drugId: r.drugId ?? draft.drugId })),
      });
    }
  };
  // Edit one taper row. A from-date or days change re-chains every FOLLOWING
  // row's from-date (prev To + 1) so start/end stay continuous — the same
  // rule the "+" append uses (mirrors the EMR dialog, 2026-10-30).
  const setTaperRow = (key: string, patch: Partial<DraftTaperRow>) => {
    if (!draft?.taper) return;
    const idx = draft.taper.findIndex((r) => r.key === key);
    if (idx < 0) return;
    const patched = draft.taper.map((r) => (r.key === key ? { ...r, ...patch } : r));
    set("taper", "fromDate" in patch || "days" in patch ? rechainTaperRows(patched, idx) : patched);
  };
  const removeTaperRow = (key: string) => {
    if (!draft?.taper) return;
    set("taper", draft.taper.filter((r) => r.key !== key));
  };
  const addTaperRow = () => {
    if (!draft?.taper) return;
    const last = draft.taper[draft.taper.length - 1];
    let fromDate = dateIST();
    if (last) {
      const to = stepToDate(last.fromDate, last.days);
      if (to) fromDate = addDays(to, 1);
    }
    const inherit = draft.perStrength && last
      ? { name: last.name || draft.name, strength: last.strength || draft.strength, composition: last.composition ?? draft.composition, formulation: last.formulation ?? draft.formulation, drugId: last.drugId ?? draft.drugId }
      : draft;
    set("taper", [...draft.taper, newTaperRow(fromDate, inherit as DraftMedicine, draft.perStrength)]);
  };

  // ---- Save (same validation as the EMR dialog) ----
  const save = () => {
    if (!draft || !medicine) return;
    if (!draft.name.trim()) { alert("Enter a drug name."); return; }
    const freq = normalizeFrequency(draft.frequency);
    if (draft.taper) {
      for (const [i, r] of draft.taper.entries()) {
        if (!r.fromDate) { alert(`Protocol row ${i + 1}: pick a From date`); return; }
        if (!r.days || parseInt(r.days, 10) < 1) { alert(`Protocol row ${i + 1}: enter the number of days`); return; }
        if (!normalizeFrequency(r.frequency) || !isValidFrequency(normalizeFrequency(r.frequency))) {
          alert(`Protocol row ${i + 1}: enter a frequency like 1-1-1-1`);
          return;
        }
        if (draft.perStrength && !r.name.trim()) {
          alert(`Protocol row ${i + 1}: pick a drug for this row`);
          return;
        }
      }
      if (draft.taper.length === 0) { alert("Add at least one protocol row"); return; }
    } else {
      if (!freq && !draft.instructions.trim()) {
        alert("Enter a frequency or instructions.");
        return;
      }
      if (!isValidFrequency(freq)) {
        alert("Frequency looks invalid — use numbers like 1-1-1 or 1.5-0-1.");
        return;
      }
    }
    const taperSteps: TaperStep[] | null = draft.taper
      ? draft.taper.map((r) => ({
          fromDate: r.fromDate,
          frequency: normalizeFrequency(r.frequency),
          days: parseInt(r.days, 10),
          instructions: r.instructions || null,
          name: draft.perStrength ? (r.name.trim() || null) : null,
          strength: draft.perStrength ? (r.strength.trim() || null) : null,
          composition: draft.perStrength ? (r.composition ?? null) : null,
          formulation: draft.perStrength ? (r.formulation ?? null) : null,
          drugId: draft.perStrength ? (r.drugId ?? null) : null,
        }))
      : null;
    onSave({
      drugId: draft.drugId,
      name: draft.name,
      composition: draft.composition,
      formulation: draft.formulation,
      strength: draft.strength || null,
      frequency: taperSteps ? null : (freq || null),
      taper: taperSteps,
      duration: taperSteps ? null : (draft.days ? `${draft.days} days` : null),
      instructions: draft.instructions || null,
    });
    onOpenChange(false);
  };

  const del = () => {
    onDelete();
    onOpenChange(false);
  };

  const cancel = () => onOpenChange(false);

  if (!draft) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader><DialogTitle>Edit medicine</DialogTitle></DialogHeader>

        {/* Drug-name search — full-width row (dropdown needs vertical space). */}
        <div className="relative">
          <Label className="text-xs">Drug name</Label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <Input
              ref={nameSearchRef}
              className="pl-9"
              value={nameQuery}
              onChange={(e) => {
                const v = e.target.value;
                setNameQuery(v);
                set("name", v);
                setShowNameDropdown(v.length >= 3);
              }}
              onFocus={() => nameQuery.length >= 3 && setShowNameDropdown(true)}
              onBlur={() => setTimeout(() => setShowNameDropdown(false), 150)}
              onKeyDown={(e) => {
                if (showNameDropdown && nameQuery.length >= 3 && !drugsLoading && editDrugListCount > 0) {
                  onEditDrugKeyDown(e);
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                }
              }}
              placeholder="Search drug name or composition (3+ chars)…"
            />
            {showNameDropdown && nameQuery.length >= 3 && (
              <div className="visible-scrollbar absolute z-50 mt-1 max-h-60 w-full overscroll-contain overflow-y-auto rounded-md border border-cream-200 bg-cream-100 shadow-lg dark:border-dark-700 dark:bg-dark-800">
                {drugsLoading ? (
                  <div className="p-2"><Skeleton className="h-6" /></div>
                ) : drugs.length === 0 ? (
                  <button
                    type="button"
                    ref={editDrugHighlight === 0 ? editDrugHighlightRef : undefined}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                      editDrugHighlight === 0 ? "bg-cream-200 dark:bg-dark-700" : ""
                    }`}
                    onMouseDown={(e) => { e.preventDefault(); setShowNameDropdown(false); }}
                  >
                    <Search size={14} className="text-muted" /> No match — keep "{nameQuery}" as entered
                  </button>
                ) : (
                  <>
                    {editDrugResults.map((d, idx) => (
                      <button
                        key={d.id}
                        type="button"
                        ref={idx === editDrugHighlight ? editDrugHighlightRef : undefined}
                        className={`flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                          idx === editDrugHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                        }`}
                        onMouseDown={(e) => { e.preventDefault(); selectDrug(d); }}
                      >
                        <span className="font-medium text-ink dark:text-cream-50">{d.name}</span>
                        <span className="text-xs text-muted">
                          {[d.composition, d.formulation, d.strength].filter(Boolean).join(" · ")}
                        </span>
                      </button>
                    ))}
                    <button
                      type="button"
                      ref={editDrugResults.length === editDrugHighlight ? editDrugHighlightRef : undefined}
                      className={`flex w-full items-center gap-2 border-t border-cream-200 px-3 py-2 text-left text-sm text-maroon-600 hover:bg-cream-200 dark:border-dark-700 dark:hover:bg-dark-700 ${
                        editDrugResults.length === editDrugHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                      }`}
                      onMouseDown={(e) => { e.preventDefault(); setShowNameDropdown(false); }}
                    >
                      Keep "{nameQuery}" as entered
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Composition / Formulation / Strength — one row, matching the
            entry dialog's compact field layout. */}
        <div className="grid grid-cols-[1fr_140px_120px] items-end gap-2">
          <div>
            <Label className="text-xs">Composition</Label>
            <Input value={draft.composition ?? ""} onChange={(e) => set("composition", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Formulation</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              value={draft.formulation ?? "Tablet"}
              onChange={(e) => set("formulation", e.target.value as DischargeMedicineRow["formulation"])}
            >
              {FORMULATIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Strength / dosage</Label>
            <Input value={draft.strength} onChange={(e) => set("strength", e.target.value)} placeholder="e.g. 500mg" />
          </div>
        </div>

        {/* Escalation / De-escalation protocol checkboxes — same as the
            add-drug draft panel. An existing taper loads straight into the
            editable grid below (same format as entry). */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="discharge-edit-taper-toggle"
            checked={!!draft.taper}
            onCheckedChange={(checked) => (checked ? enableTaper() : disableTaper())}
          />
          <Label htmlFor="discharge-edit-taper-toggle" className="text-xs cursor-pointer">
            Escalation / De-escalation protocol
          </Label>
          {draft.taper && (
            <>
              <Checkbox
                id="discharge-edit-per-strength-toggle"
                className="ml-4"
                checked={draft.perStrength}
                onCheckedChange={(checked) => togglePerStrength(checked === true)}
              />
              <Label htmlFor="discharge-edit-per-strength-toggle" className="text-xs cursor-pointer">
                Allow different strengths per row
              </Label>
            </>
          )}
        </div>

        {draft.taper ? (
          /* Editable taper grid — identical to the EMR dialog's. */
          <div className="space-y-1.5">
            {draft.perStrength ? (
              <div className={`${TAPER_GRID_DRUG} text-xs text-muted`}>
                <span>Drug</span>
                <span>From date</span>
                <span>Frequency</span>
                <span>Days</span>
                <span>To date</span>
                <span>Instructions</span>
                <span className="text-center">+</span>
                <span className="text-center">×</span>
              </div>
            ) : (
              <div className={`${TAPER_GRID} text-xs text-muted`}>
                <span>From date</span>
                <span>Frequency</span>
                <span>Days</span>
                <span>To date</span>
                <span>Instructions</span>
                <span className="text-center">+</span>
                <span className="text-center">×</span>
              </div>
            )}
            {draft.taper.map((r) => {
              const to = stepToDate(r.fromDate, r.days);
              return (
                <div key={r.key} className={draft.perStrength ? TAPER_GRID_DRUG : TAPER_GRID}>
                  {draft.perStrength && (
                    <TaperRowDrugSearch
                      rowKey={r.key}
                      value={r.name}
                      onPick={(d) => setTaperRow(r.key, {
                        drugId: d.id || null,
                        name: d.name,
                        strength: d.strength ?? r.strength,
                        composition: d.composition ?? r.composition,
                        formulation: (d.formulation as DischargeMedicineRow["formulation"]) ?? r.formulation,
                      })}
                    />
                  )}
                  <Input
                    type="date"
                    className="h-8"
                    value={r.fromDate}
                    onChange={(e) => setTaperRow(r.key, { fromDate: e.target.value })}
                  />
                  <DischargeFrequencyInput
                    className="h-8"
                    value={r.frequency}
                    onChange={(v) => setTaperRow(r.key, { frequency: v })}
                  />
                  <Input
                    type="number"
                    min={1}
                    className="h-8"
                    value={r.days}
                    onChange={(e) => setTaperRow(r.key, { days: e.target.value })}
                  />
                  <div className="flex h-8 items-center gap-1 text-sm text-muted">
                    <CalendarDays size={13} className="shrink-0" />
                    <span className="whitespace-nowrap">{to ? fmtDate(to) : "—"}</span>
                  </div>
                  <Input
                    className="h-8"
                    placeholder="With breakfast"
                    value={r.instructions}
                    onChange={(e) => setTaperRow(r.key, { instructions: e.target.value })}
                  />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-maroon-600" onClick={addTaperRow} title="Add next row">
                    <Plus size={14} />
                  </Button>
                  <Button
                    size="icon" variant="ghost" className="h-8 w-8 text-red-600"
                    onClick={() => removeTaperRow(r.key)}
                    disabled={draft.taper!.length === 1}
                    title="Remove row"
                  >
                    <X size={14} />
                  </Button>
                </div>
              );
            })}
            <div className="flex items-center gap-1.5 text-xs text-muted">
              <Plus size={12} className="text-maroon-600" />
              {draft.perStrength
                ? <>Use <b>+</b> to append a row. Search a different strength in each row's Drug box; consecutive same-strength rows print under one header.</>
                : <>Use <b>+</b> on a row to append the next period (its From date starts the day after the previous To date).</>}
            </div>
          </div>
        ) : (
          /* Simple drug row — Frequency | Days | Instructions (same as the
             add-drug draft panel, minus the Add button which is in the
             dialog footer here). */
          <div className="grid grid-cols-[180px_110px_1fr] items-end gap-2"
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}>
            <div>
              <Label className="text-xs">Frequency</Label>
              <DischargeFrequencyInput
                value={draft.frequency}
                onChange={(v) => set("frequency", v)}
                onCommit={save}
              />
            </div>
            <div>
              <Label className="text-xs">Days</Label>
              <Input
                type="number"
                min={1}
                value={draft.days}
                onChange={(e) => set("days", e.target.value)}
                placeholder="5"
              />
            </div>
            <div>
              <Label className="text-xs">Instructions</Label>
              <Input
                value={draft.instructions}
                onChange={(e) => set("instructions", e.target.value)}
                placeholder="e.g. After meals (optional)"
              />
            </div>
          </div>
        )}

        {draft.taper && (
          <div className="flex items-center gap-1.5 text-xs text-muted">
            <Plus size={12} className="text-maroon-600" />
            Days and frequency come from the protocol rows above.
          </div>
        )}

        {/* Footer: Delete on the left, Cancel + Save on the right.
            Escape cancels via Radix Dialog's built-in onOpenChange(false). */}
        <div className="flex justify-between">
          <Button variant="ghost" className="text-red-600" onClick={del}>
            <Trash size={14} className="mr-1" /> Delete medicine
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={cancel}>Cancel</Button>
            <Button onClick={save} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
